import { ITemplateVariable } from '@g99-product-web-ui/shared-interface-lib';

import { formatUsPhoneNumber } from './phone-number-formatter.util';

export const CLINIC_CONTACT_NUMBER_VARIABLE = 'CLINIC_CONTACT_NUMBER';

/**
 * Returns the value for the provided template variable.
 *
 * If the variable key exists in the provided `variablesData`, its corresponding value is returned.
 * A resolved clinic contact number is formatted as a US phone number.
 * Otherwise, it returns a default formatted string using the variable key in the form: ' ${<key>} '.
 *
 * @param variable - The full ITemplateVariable object containing the key info.
 * @param variablesData - A map of variable keys and their corresponding values.
 * @returns The resolved value from `variablesData` or a default formatted string.
 */
export function getTemplateVariableValue(
  variable: ITemplateVariable,
  variablesData: Record<string, string>
): string {
  const key = variable.variable;
  const value = variablesData[key];
  if (value == null) {
    return `\${${key}}`;
  }
  return key === CLINIC_CONTACT_NUMBER_VARIABLE
    ? formatUsPhoneNumber(value)
    : value;
}

/**
 * Normalizes an unresolved template-variable token to the canonical stored
 * form: spaces replaced with underscores. This is the single source of truth
 * for how a token is encoded, so the backend resolves it identically wherever
 * it lands (email subject, email body, SMS).
 *
 * Only inputs that are a whole `${...}` token are normalized. Resolved values
 * (e.g. a business name like `Akshita Test New UI`) are returned unchanged so
 * their spaces are preserved.
 *
 * @param token - The raw variable token, e.g. `${First Name}`, or a resolved value.
 * @returns The normalized token, e.g. `${First_Name}`, or the input unchanged
 * if it is not an unresolved token.
 */
export function normalizeVariableToken(token: string): string {
  if (!/^\$\{[^}]*\}$/.test(token)) {
    return token;
  }
  return token.replace(/ /g, '_');
}
